import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Truck, Loader2, MapPin, Route, Package, Gauge, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STAFF_ROLES = ["deal_manager", "admin", "super_admin", "compliance_director", "senior_verification_manager", "compliance_reviewer"];

function statusCls(s) {
  if (["assigned", "carrier_accepted", "carrier_selected"].includes(s)) return "bg-emerald-100 text-emerald-700";
  if (["no_carrier_available", "offer_expired", "carrier_declined"].includes(s)) return "bg-red-100 text-red-700";
  if (["carrier_offered", "qualified_carriers_found", "quote_submitted"].includes(s)) return "bg-blue-100 text-blue-700";
  return "bg-slate-100 text-slate-600";
}

export default function LoadsPanel({ deal }) {
  const { user } = useAuth();
  const isStaff = STAFF_ROLES.includes(user?.role);
  const isParty = [deal.buyer_user_id, deal.seller_user_id].includes(user?.user_id);
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/deals/${deal.deal_id}/loads`);
      setLoads(data.loads || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [deal.deal_id]);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); if (ok) toast.success(ok); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const create = () => act("create", () => api.post(`/deals/${deal.deal_id}/loads`, {
    commodity: deal.commodity, description: deal.description, weight: 10000, equipment_type: "dry_van",
    pickup_location: { label: deal.origin }, delivery_location: { label: deal.destination },
    delivery_deadline: deal.delivery_date, insurance_required: 0,
  }), "Load created");
  const search = (l) => act(`s-${l.load_id}`, () => api.post(`/loads/${l.load_id}/search`), "Carrier pool built");
  const offer = (l, bid) => act(`o-${l.load_id}`, () => api.post(`/loads/${l.load_id}/offer`, { business_id: bid, method: "direct" }), "Offered to carrier");
  const accept = (l) => act(`a-${l.load_id}`, () => api.post(`/loads/${l.load_id}/accept`), "Load accepted");
  const decline = (l) => act(`d-${l.load_id}`, () => api.post(`/loads/${l.load_id}/decline`), "Load declined");

  return (
    <div className="mt-6 border-t border-slate-200 pt-5" data-testid="loads-panel">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Truck size={15} /> Loads & carrier coordination</h4>
        {(isStaff || isParty) && (
          <button data-testid="load-create-btn" onClick={create} disabled={busy === "create"}
            className="rounded-md bg-[#2563EB] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60">Create load</button>
        )}
      </div>

      {loading ? <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div> : (
        <div className="space-y-3" data-testid="loads-list">
          {loads.length === 0 && <p className="text-sm text-slate-400">No loads yet.</p>}
          {loads.map((l) => (
            <div key={l.load_id} data-testid={`load-${l.load_id}`} className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm font-medium text-[#0A0F1C]"><Package size={14} className="text-slate-400" /> {l.commodity} · {l.equipment_type || "—"}</span>
                <span data-testid={`load-status-${l.load_id}`} className={`rounded px-2 py-0.5 text-xs ${statusCls(l.match_status)}`}>{l.match_status?.replace(/_/g, " ")}</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-4 text-[11px] text-slate-500">
                <span className="flex items-center gap-1"><MapPin size={10} /> {l.pickup_location?.label || "?"} → {l.delivery_location?.label || "?"}</span>
                {l.delivery_deadline && <span>Deadline: {l.delivery_deadline}</span>}
              </div>

              {/* Ranked carrier pool + match explanations (staff-facing fair-opportunity) */}
              {l.ranked?.length > 0 && (
                <div className="mt-2" data-testid={`load-pool-${l.load_id}`}>
                  <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase text-slate-400"><Gauge size={11} /> Ranked carriers</div>
                  <ul className="space-y-0.5 text-[11px] text-slate-500">
                    {l.ranked.slice(0, 4).map((c, i) => (
                      <li key={i} className="flex items-center justify-between gap-2">
                        <span>#{i + 1} {c.business_id}{c.is_new ? " (new)" : ""} — {c.distance_mi != null ? `${c.distance_mi}mi, ` : ""}workload {c.workload}</span>
                        {isStaff && (l.match_status === "qualified_carriers_found") && (
                          <button data-testid={`load-offer-${l.load_id}-${c.business_id}`} onClick={() => offer(l, c.business_id)} disabled={busy === `o-${l.load_id}`}
                            className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-white">Offer</button>
                        )}
                      </li>
                    ))}
                  </ul>
                  {l.excluded?.length > 0 && <p className="mt-1 text-[11px] text-slate-400" data-testid={`load-excluded-${l.load_id}`}>{l.excluded.length} excluded by eligibility gate</p>}
                </div>
              )}

              {/* Offers */}
              {l.offers?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1" data-testid={`load-offers-${l.load_id}`}>
                  {l.offers.map((o, i) => <span key={i} className={`rounded px-1.5 py-0.5 text-[11px] ${statusCls(o.status)}`}>{o.business_id?.slice(0, 10)}: {o.status}</span>)}
                </div>
              )}

              {/* Milestones */}
              {l.milestones?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-500" data-testid={`load-milestones-${l.load_id}`}>
                  {l.milestones.map((m, i) => <span key={i} className="flex items-center gap-1"><Route size={10} /> {m.milestone}: {m.status}</span>)}
                </div>
              )}
              {l.shipment_id && <p className="mt-1 text-[11px] text-emerald-700" data-testid={`load-shipment-${l.load_id}`}>Shipment {l.shipment_id} created · GPS requires consent (SIMULATED)</p>}

              <div className="mt-3 flex flex-wrap gap-1.5">
                {isStaff && ["load_created", "carrier_declined", "offer_expired"].includes(l.match_status) && (
                  <button data-testid={`load-search-${l.load_id}`} onClick={() => search(l)} disabled={busy === `s-${l.load_id}`}
                    className="h-7 rounded bg-slate-800 px-2 text-xs text-white">Search carriers</button>
                )}
                {["carrier_offered", "offer_viewed", "reassigned"].includes(l.match_status) && (
                  <>
                    <button data-testid={`load-accept-${l.load_id}`} onClick={() => accept(l)} disabled={busy === `a-${l.load_id}`}
                      className="h-7 rounded bg-[#65A30D] px-2 text-xs text-white">Accept load</button>
                    <button data-testid={`load-decline-${l.load_id}`} onClick={() => decline(l)} disabled={busy === `d-${l.load_id}`}
                      className="h-7 rounded border border-slate-300 px-2 text-xs text-slate-600">Decline</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="mt-3 flex items-center gap-1 text-[11px] text-amber-700"><ShieldAlert size={11} /> Precise truck/driver location is redacted until authorized — SIMULATED, no live carrier payments.</p>
    </div>
  );
}
